export const createResizeHandles = (
  textEditorWrapper: HTMLDivElement,
  stageContainer?: HTMLDivElement
): HTMLDivElement[] => {
  const handlePositions = [
    { name: 'middle-left', cursor: 'ew-resize', position: { top: 'calc(50% - 6px)', left: '-6px' } },
    { name: 'middle-right', cursor: 'ew-resize', position: { top: 'calc(50% - 6px)', right: '-6px' } },
  ];

  const resizeHandles: HTMLDivElement[] = [];

  handlePositions.forEach((handleConfig) => {
    const handle = document.createElement('div');
    handle.style.position = 'absolute';
    handle.style.width = '12px';
    handle.style.height = '12px';
    handle.style.background = '#fff';
    handle.style.border = '2px solid #1890ff';
    handle.style.borderRadius = '2px';
    handle.style.cursor = handleConfig.cursor;
    handle.style.zIndex = '1001';
    Object.assign(handle.style, handleConfig.position);
    handle.dataset.handle = handleConfig.name;
    textEditorWrapper.appendChild(handle);
    resizeHandles.push(handle);

    handle.addEventListener('mousedown', (e) => {
      e.stopPropagation();
      const startX = e.clientX;
      const startWidth = textEditorWrapper.offsetWidth;
      const startLeft = textEditorWrapper.offsetLeft;

      const handleResize = (e: MouseEvent) => {
        const deltaX = e.clientX - startX;
        const handleName = handleConfig.name;

        let newWidth = startWidth;
        let newLeft = startLeft;
        let MIN_WIDTH = 1;
        const textArea = textEditorWrapper.querySelector('textarea') as HTMLTextAreaElement | null;
        if (textArea) {
          const computed = window.getComputedStyle(textArea);
          const fontSizePx = parseFloat(computed.fontSize || '14');
          const paddingLeft = parseFloat(computed.paddingLeft || '0');
          const paddingRight = parseFloat(computed.paddingRight || '0');
          const charWidth = fontSizePx * 0.6; // approximate average char width
          MIN_WIDTH = Math.max(1, charWidth + paddingLeft + paddingRight);
        } else {
          MIN_WIDTH = 16;
        }

        if (handleName.includes('right')) {
          newWidth = Math.max(MIN_WIDTH, startWidth + deltaX);
        }
        if (handleName.includes('left')) {
          newWidth = Math.max(MIN_WIDTH, startWidth - deltaX);
          newLeft = startLeft + (startWidth - newWidth);
        }

        // Apply canvas boundary constraints
        if (stageContainer) {
          const stageRect = stageContainer.getBoundingClientRect();

          // Constrain width and horizontal position
          if (handleName.includes('right')) {
            const maxWidth = stageRect.right - startLeft;
            newWidth = Math.min(newWidth, maxWidth);
          }
          if (handleName.includes('left')) {
            const minLeft = stageRect.left;
            if (newLeft < minLeft) {
              newWidth = newWidth - (minLeft - newLeft);
              newLeft = minLeft;
            }
          }
        }
        textEditorWrapper.style.width = newWidth + 'px';
        textEditorWrapper.style.left = newLeft + 'px';

        const textArea2 = textEditorWrapper.querySelector('textarea');
        if (textArea2 && stageContainer) {
          textArea2.style.width = '100%';
          void textArea2.offsetHeight;

          const newContentHeight = textArea2.scrollHeight;
          const stageRect = stageContainer.getBoundingClientRect();
          const currentTop = textEditorWrapper.offsetTop;
          const availableHeight = Math.max(1, stageRect.bottom - currentTop);

          const finalHeight = Math.min(newContentHeight, availableHeight);
          textEditorWrapper.style.maxHeight = availableHeight + 'px';
          textEditorWrapper.style.height = finalHeight + 'px';
          textArea2.style.height = finalHeight + 'px';

          textArea2.dispatchEvent(new Event('input', { bubbles: true }));
        }
      };

      const stopResize = () => {
        document.removeEventListener('mousemove', handleResize);
        document.removeEventListener('mouseup', stopResize);
      };

      document.addEventListener('mousemove', handleResize);
      document.addEventListener('mouseup', stopResize);
    });
  });

  return resizeHandles;
};

export const setupDraggableBehavior = (
  textEditorWrapper: HTMLDivElement,
  textEditorInput: HTMLTextAreaElement,
  resizeHandles: HTMLDivElement[],
  stageContainer?: HTMLDivElement
): void => {
  let isDragging = false;
  let dragStartX = 0;
  let dragStartY = 0;
  let wrapperStartX = 0;
  let wrapperStartY = 0;

  textEditorWrapper.addEventListener('mousedown', (e) => {
    if (e.target === textEditorInput || resizeHandles.some((h) => h === e.target)) return;
    isDragging = true;
    dragStartX = e.clientX;
    dragStartY = e.clientY;
    wrapperStartX = textEditorWrapper.offsetLeft;
    wrapperStartY = textEditorWrapper.offsetTop;

    // Prevent default to avoid text selection during drag
    e.preventDefault();
  });

  document.addEventListener('mousemove', (e) => {
    if (!isDragging) return;
    const deltaX = e.clientX - dragStartX;
    const deltaY = e.clientY - dragStartY;

    let newLeft = wrapperStartX + deltaX;
    let newTop = wrapperStartY + deltaY;

    // Apply canvas boundary constraints if stage container is provided
    if (stageContainer) {
      const stageRect = stageContainer.getBoundingClientRect();
      const wrapperWidth = textEditorWrapper.offsetWidth;
      const wrapperHeight = textEditorWrapper.offsetHeight;

      // Constrain to stage boundaries
      newLeft = Math.max(stageRect.left, Math.min(newLeft, stageRect.right - wrapperWidth));
      newTop = Math.max(stageRect.top, Math.min(newTop, stageRect.bottom - wrapperHeight));
    }

    textEditorWrapper.style.left = newLeft + 'px';
    textEditorWrapper.style.top = newTop + 'px';
  });

  document.addEventListener('mouseup', () => {
    isDragging = false;
  });
};
